// src/pages/PrivacyPolicy.js
import React from 'react';
import { ArrowLeft, Shield, Heart, Eye, Lock, Users, MessageCircle } from 'lucide-react';

const PrivacyPolicy = () => {
  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-purple-100">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={handleGoBack}
              className="flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Privacy Policy</h1>
                <p className="text-sm text-gray-600">Protecting your child's privacy and safety</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-xl border border-purple-100 overflow-hidden">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <Heart className="w-8 h-8" />
              <h2 className="text-3xl font-bold">Our Commitment to Children's Privacy</h2>
            </div>
            <p className="text-lg text-purple-100">
              At TheOliveTree, we are committed to protecting the privacy and safety of children who use our educational app. 
              We comply with COPPA, GDPR, and other applicable privacy laws.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <div className="bg-white/20 rounded-full px-4 py-2">
                <strong>Effective Date:</strong> September 1, 2025
              </div>
              <div className="bg-white/20 rounded-full px-4 py-2">
                <strong>Last Updated:</strong> September 1, 2025
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-8 prose prose-lg max-w-none">
            
            {/* Section 1 */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-blue-100 p-3 rounded-full">
                  <Eye className="w-6 h-6 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 m-0">Information We Collect</h2>
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <h3 className="text-xl font-semibold text-blue-800 mb-3">Information Collected from Children</h3>
                <p className="text-blue-700 mb-4">We only collect information that is necessary to provide our educational services:</p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-blue-800">Account Information:</h4>
                    <ul className="list-disc list-inside text-blue-700 ml-4">
                      <li>Child's first name (for personalization)</li>
                      <li>Age or date of birth (for age-appropriate content)</li>
                      <li>Learning progress and game scores</li>
                      <li>Time spent in activities</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-blue-800">Educational Data:</h4>
                    <ul className="list-disc list-inside text-blue-700 ml-4">
                      <li>Quiz and game results</li>
                      <li>Learning achievements and milestones</li>
                      <li>Areas of strength and difficulty</li>
                      <li>Session duration and frequency</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-blue-800">Technical Information:</h4>
                    <ul className="list-disc list-inside text-blue-700 ml-4">
                      <li>Device type and operating system</li>
                      <li>App crashes and error reports (anonymized)</li>
                      <li>Usage analytics (aggregated and non-personal)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Information Collected from Parents</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-green-800">Required Information:</h4>
                    <ul className="list-disc list-inside text-green-700 ml-4">
                      <li>Parent/guardian email address</li>
                      <li>Parent/guardian name</li>
                      <li>Verification of parental consent</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-green-800">Optional Information:</h4>
                    <ul className="list-disc list-inside text-green-700 ml-4">
                      <li>Feedback and support communications</li>
                      <li>Subscription and payment information (processed by third-party payment processors)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-6">
                <h3 className="text-xl font-semibold text-red-800 mb-3">Information We Do NOT Collect</h3>
                <p className="text-red-700 mb-4">We do not collect:</p>
                <ul className="list-disc list-inside text-red-700 ml-4 space-y-1">
                  <li>Home address or precise location</li>
                  <li>Phone numbers</li>
                  <li>School information</li>
                  <li>Social security numbers or government IDs</li>
                  <li>Any personal information not necessary for educational services</li>
                </ul>
              </div>
            </div>

            {/* AI Chat Section */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-purple-100 p-3 rounded-full">
                  <MessageCircle className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 m-0">AI Chat Feature Limitations</h2>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-6">
                <div className="bg-yellow-100 border border-yellow-400 rounded-lg p-4 mb-6">
                  <p className="text-yellow-800 font-semibold">⚠️ Important Safety Measure</p>
                  <p className="text-yellow-700">Our app includes limited AI chat functionality designed specifically for educational purposes.</p>
                </div>

                <h3 className="text-xl font-semibold text-purple-800 mb-4">To ensure child safety:</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-500 rounded-full p-1 mt-1">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Strictly Educational</h4>
                        <p className="text-purple-700 text-sm">AI interactions are limited to learning Arabic language concepts only</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="bg-green-500 rounded-full p-1 mt-1">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">No Personal Conversations</h4>
                        <p className="text-purple-700 text-sm">Children cannot share personal information through chat</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="bg-green-500 rounded-full p-1 mt-1">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Monitored Content</h4>
                        <p className="text-purple-700 text-sm">All AI responses are filtered and monitored for appropriate content</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-500 rounded-full p-1 mt-1">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Parental Controls</h4>
                        <p className="text-purple-700 text-sm">Parents can disable AI chat features entirely</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="bg-green-500 rounded-full p-1 mt-1">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">No Data Storage</h4>
                        <p className="text-purple-700 text-sm">AI chat conversations are not stored or saved</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="bg-green-500 rounded-full p-1 mt-1">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-800">Age-Appropriate</h4>
                        <p className="text-purple-700 text-sm">AI responses are designed for child comprehension levels</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 bg-purple-100 rounded-lg p-4">
                  <p className="text-purple-800 font-medium">
                    📱 Parents can review and control AI chat settings in their dashboard at any time.
                  </p>
                </div>
              </div>
            </div>

            {/* Data Usage */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">How We Use Information</h2>
              <p className="text-gray-700 mb-6">We use collected information solely to:</p>
              
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-blue-800 mb-3">Provide Educational Services:</h3>
                  <ul className="list-disc list-inside text-blue-700 space-y-1">
                    <li>Personalize learning experiences</li>
                    <li>Track educational progress</li>
                    <li>Provide age-appropriate content</li>
                    <li>Generate progress reports for parents</li>
                  </ul>
                </div>
                
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-green-800 mb-3">Improve Our Service:</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-1">
                    <li>Analyze learning effectiveness (using aggregated, anonymous data)</li>
                    <li>Fix technical issues and bugs</li>
                    <li>Develop new educational content</li>
                  </ul>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold text-purple-800 mb-3">Communicate with Parents:</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-1">
                    <li>Send progress updates</li>
                    <li>Provide customer support</li>
                    <li>Send important service announcements</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Data Sharing */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Information Sharing and Disclosure</h2>
              
              <div className="bg-red-100 border-2 border-red-300 rounded-lg p-6 mb-6">
                <p className="text-red-800 font-bold text-xl text-center">
                  🛡️ We do not sell, rent, or trade children's personal information to third parties.
                </p>
              </div>

              <p className="text-gray-700 mb-4">We may share information only in these limited circumstances:</p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-blue-500 rounded-full p-2 mt-1">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Service Providers</h4>
                    <p className="text-gray-700">With trusted third-party service providers who help us operate our app (such as cloud hosting, analytics, and payment processing), but only under strict confidentiality agreements.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="bg-yellow-500 rounded-full p-2 mt-1">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Legal Requirements</h4>
                    <p className="text-gray-700">If required by law, court order, or to protect the safety of children or others.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="bg-green-500 rounded-full p-2 mt-1">
                    <Heart className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800">Parental Request</h4>
                    <p className="text-gray-700">With parents/guardians upon verification of their identity.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-100 p-3 rounded-full">
                  <Lock className="w-6 h-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 m-0">Data Security</h2>
              </div>

              <p className="text-gray-700 mb-6">We implement industry-standard security measures:</p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800">🔐 Encryption</h4>
                    <p className="text-green-700 text-sm">All data is encrypted in transit and at rest</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800">🔑 Access Controls</h4>
                    <p className="text-blue-700 text-sm">Limited employee access on a need-to-know basis</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800">🔍 Regular Audits</h4>
                    <p className="text-purple-700 text-sm">Security reviews and vulnerability assessments</p>
                  </div>
                  
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-800">🎓 Staff Training</h4>
                    <p className="text-orange-700 text-sm">All employees trained on children's privacy protection</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Parental Rights */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Parental Rights and Controls</h2>
              <p className="text-gray-700 mb-6">Parents have complete control over their child's information:</p>
              
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">✓</span>
                      <span className="font-semibold text-gray-800">Access</span>
                    </div>
                    <p className="text-gray-700 text-sm ml-9">View all information we have collected about your child</p>
                    
                    <div className="flex items-center gap-3">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">✓</span>
                      <span className="font-semibold text-gray-800">Correction</span>
                    </div>
                    <p className="text-gray-700 text-sm ml-9">Update or correct any inaccurate information</p>
                    
                    <div className="flex items-center gap-3">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">✓</span>
                      <span className="font-semibold text-gray-800">Deletion</span>
                    </div>
                    <p className="text-gray-700 text-sm ml-9">Delete your child's account and all associated data</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">✓</span>
                      <span className="font-semibold text-gray-800">Control</span>
                    </div>
                    <p className="text-gray-700 text-sm ml-9">Disable specific features like AI chat or data sharing</p>
                    
                    <div className="flex items-center gap-3">
                      <span className="bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">✓</span>
                      <span className="font-semibold text-gray-800">Review</span>
                    </div>
                    <p className="text-gray-700 text-sm ml-9">Access detailed progress reports and activity logs</p>
                  </div>
                </div>
                
                <div className="mt-6 bg-white rounded-lg p-4">
                  <p className="text-gray-800 font-medium">
                    📧 To exercise these rights, contact us at: <span className="text-blue-600">admin@my-roots.ca</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Contact Us</h2>
              
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-purple-800 mb-3">Privacy Questions</h3>
                    <p className="text-purple-700">📧 admin@my-roots.ca</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-purple-800 mb-3">General Support</h3>
                    <p className="text-purple-700">📧 admin@ymy-roots.ca</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-purple-800 mb-3">Phone</h3>
                    <p className="text-purple-700">📞 +1-343-333-7579</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-purple-800 mb-3">Mail</h3>
                    <p className="text-purple-700">📮 North Vancouver, BC, Canada</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            This Privacy Policy is designed to be transparent and understandable. If you have any questions about how we protect your child's privacy, please contact us.
          </p>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;